import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from './AuthContext';
import { formsApi } from '../api/api';

interface LanguageContextType {
  currentLanguage: string;
  changeLanguage: (language: string) => Promise<void>;
  isLoading: boolean;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

export const LanguageProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { i18n } = useTranslation();
  const { user } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [currentLanguage, setCurrentLanguage] = useState(i18n.language);

  // Save language preference to backend for authenticated users
  const saveLanguagePreference = useCallback(async (language: string) => {
    if (user) {
      try {
        await formsApi.post('/user/language-preference/', { language });
        localStorage.setItem('userLanguagePreference', language);
      } catch (error) {
        console.error('Failed to save language preference:', error);
      }
    }
  }, [user]);

  // Load user's language preference when they log in
  useEffect(() => {
    const loadUserLanguagePreference = async () => {
      if (user) {
        try {
          // Try to get user's language preference from backend
          const response = await formsApi.get('/user/language-preference/');
          const userLanguage = response.data.language;
          
          if (userLanguage && userLanguage !== i18n.language) {
            await i18n.changeLanguage(userLanguage);
            setCurrentLanguage(userLanguage);
            // Store in localStorage with special key for user preference
            localStorage.setItem('userLanguagePreference', userLanguage);
          }
        } catch (error) {
          // If no preference stored on backend or endpoint doesn't exist, use current language as default
          console.log('No language preference found or endpoint unavailable, using current language');
          // Try to save current language to backend (only if endpoint exists)
          try {
            await saveLanguagePreference(i18n.language);
          } catch (saveError) {
            // Silently handle if the backend endpoint doesn't exist yet
            console.log('Language preference endpoint not available yet');
          }
        }
      } else {
        // User logged out, clear user preference but keep anonymous preference
        localStorage.removeItem('userLanguagePreference');
      }
    };

    if (user !== null) { // Only run when auth state is determined
      loadUserLanguagePreference();
    }
  }, [user, i18n, saveLanguagePreference]);

  const changeLanguage = async (language: string) => {
    setIsLoading(true);
    try {
      await i18n.changeLanguage(language);
      setCurrentLanguage(language);
      
      if (user) {
        // For authenticated users, save to backend
        await saveLanguagePreference(language);
      } else {
        // For anonymous users, just save to localStorage
        localStorage.setItem('i18nextLng', language);
      }
    } catch (error) {
      console.error('Failed to change language:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Listen to i18n language changes
  useEffect(() => {
    const handleLanguageChange = (lng: string) => {
      setCurrentLanguage(lng);
    };

    i18n.on('languageChanged', handleLanguageChange);
    return () => {
      i18n.off('languageChanged', handleLanguageChange);
    };
  }, [i18n]);

  const value = {
    currentLanguage,
    changeLanguage,
    isLoading,
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};